import { DOCUMENT, inject, RendererFactory2, Service } from '@angular/core';

import { getDomain } from '@ideals/utils';

interface LogRocketInitOptions {
  readonly browser?: {
    readonly urlSanitizer?: (url: string) => string;
  };
  readonly dom?: {
    readonly inputSanitizer?: boolean;
    readonly textSanitizer?: boolean;
  };
  readonly network?: {
    readonly requestSanitizer?: (request: Request) => Request;
    readonly responseSanitizer?: (response: Response) => Response;
  };
  readonly rootHostname?: string;
}

declare global {
  interface Window {
    readonly LogRocket: {
      readonly captureException: (error: Error) => void;
      readonly init: (key: string, options: LogRocketInitOptions) => void;
    };
  }
}

@Service()
export class LogRocketService {
  readonly #document = inject(DOCUMENT);
  readonly #renderer2 = inject(RendererFactory2).createRenderer(null, null);

  #appId?: string;

  captureException(error: Error): void {
    if (!this.#appId) {
      return;
    }

    window.LogRocket?.captureException(error);
  }

  initialize(appId?: string): void {
    if (!appId) {
      return;
    }

    this.#appId = appId;
    this.#createScript(appId);
  }

  #createScript(appId: string): void {
    const script = this.#renderer2.createElement('script') as HTMLScriptElement;
    const scriptSrc = 'https://cdn.lgrckt-in.com/LogRocket.min.js';

    this.#renderer2.setAttribute(script, 'async', '');
    this.#renderer2.setAttribute(script, 'src', scriptSrc);
    this.#renderer2.setAttribute(script, 'crossorigin', 'anonymous');
    this.#renderer2.appendChild(this.#document.head, script);

    script.onload = () => {
      window.LogRocket.init(appId, {
        network: {
          requestSanitizer: (request) => {
            return {
              ...request,
              body: null,
            };
          },
          responseSanitizer: (response) => {
            return {
              ...response,
              body: null,
            };
          },
        },
        rootHostname: `.${getDomain()}`,
      });
    };

    this.#renderer2.appendChild(this.#document.head, script);
  }
}
